/**
 * Every build-time setting, read once and validated here.
 *
 * Before this module the project ref and anon key were baked into a generated
 * `utils/supabase/info.tsx` as literals, so there was no way to build the same
 * source against a second backend. Staging needs exactly that: the same
 * frontend pointed at its own edge function.
 *
 * Values come from `.env` files (see `.env.example`). Vite inlines them at
 * build time, so a missing one is a build error rather than a runtime surprise.
 */

function required(name: keyof ImportMetaEnv): string {
  const value = import.meta.env[name];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(
      `Missing required environment variable ${name}. Copy .env.example to .env.local and fill it in.`
    );
  }
  return value.trim();
}

function optional(name: keyof ImportMetaEnv, fallback = ''): string {
  const value = import.meta.env[name];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : fallback;
}

const projectId = required('VITE_SUPABASE_PROJECT_ID');
const apiFunction = optional('VITE_API_FUNCTION', 'make-server-a1a36e6d');

export const env = {
  projectId,
  anonKey: required('VITE_SUPABASE_ANON_KEY'),

  supabaseUrl: `https://${projectId}.supabase.co`,
  /** Base URL of the API. Every request in `src/api` is built from this. */
  apiBaseUrl: `https://${projectId}.supabase.co/functions/v1/${apiFunction}`,
  apiFunction,

  gaMeasurementId: optional('VITE_GA_MEASUREMENT_ID'),
  googleAdsId: optional('VITE_GOOGLE_ADS_ID'),
  googleAdsLeadLabel: optional('VITE_GOOGLE_ADS_LEAD_LABEL'),
  /** Opt-in. See setEnhancedConversionData in utils/analytics.ts for what must be true first. */
  enhancedConversions: optional('VITE_ENHANCED_CONVERSIONS') === 'true',

  deployEnv: optional('VITE_DEPLOY_ENV', 'development') as ImportMetaEnv['VITE_DEPLOY_ENV'],
  isProduction: optional('VITE_DEPLOY_ENV', 'development') === 'production',
  isStaging: optional('VITE_DEPLOY_ENV', 'development') === 'staging',
} as const;
